import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Pin, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { FadeUp, MaskedLine } from "../components/motion";
import { ShareButtons } from "../components/ShareButtons";
import { SEO } from "../components/SEO";

const PER_PAGE = 25;
// Recherche insensible aux accents (é = e) et à la casse
const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function Blog() {
  const { lang, t, ts } = useLang();
  const [posts, setPosts] = useState(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, []);

  const lp = (p) => (lang === "en" && p.title_en ? { title: p.title_en, excerpt: p.excerpt_en || p.excerpt } : { title: p.title, excerpt: p.excerpt });

  const filtered = useMemo(() => {
    if (!posts) return null;
    const q = norm(query.trim());
    if (!q) return posts;
    return posts.filter((p) =>
      [p.title, p.title_en, p.excerpt, p.excerpt_en, p.seo_keywords, p.seo_keywords_en]
        .some((f) => norm(f).includes(q)));
  }, [posts, query]);

  const pages = filtered ? Math.max(1, Math.ceil(filtered.length / PER_PAGE)) : 1;
  const cur = Math.min(page, pages);
  const shown = filtered ? filtered.slice((cur - 1) * PER_PAGE, cur * PER_PAGE) : null;

  const goPage = (n) => {
    setPage(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div data-testid="blog-page" className="pt-32 pb-28">
      <SEO title={ts("blog.seo_title")} description={ts("blog.seo_desc")} path="/blogue"
        jsonLd={{ "@context": "https://schema.org", "@type": "Blog",
                  name: ts("blog.seo_title"), url: `https://mrtrophee.ca/blogue`,
                  inLanguage: lang === "fr" ? "fr-CA" : "en-CA",
                  isPartOf: { "@type": "WebSite", url: "https://mrtrophee.ca" },
                  publisher: { "@id": "https://mrtrophee.ca/#entreprise" } }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("blog.kicker")}</p>
        </FadeUp>
        <h1 className="font-serif text-5xl md:text-6xl mb-8">
          <MaskedLine>{t("blog.title_1")}</MaskedLine>{" "}
          <MaskedLine delay={0.1}><span className="italic text-gold">{t("blog.title_2")}</span></MaskedLine>
        </h1>
        <div className="mb-10">
          <ShareButtons title={`${ts("blog.title_1")} ${ts("blog.title_2")} — Mr Trophée`} label={t("footer.share_page")} />
        </div>

        {/* Recherche dans les articles (titre, résumé, mots-clés — accents ignorés) */}
        <FadeUp delay={0.15}>
          <div className="relative max-w-xl mb-12">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ivory/40 pointer-events-none" />
            <input
              type="search"
              value={query}
              data-testid="blog-search-input"
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder={lang === "fr" ? "Rechercher un article… (ex. hockey, gravure, mariage)" : "Search articles… (e.g. hockey, engraving, wedding)"}
              className="w-full bg-surface border border-ivory/15 rounded-full pl-11 pr-5 py-3 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>
        </FadeUp>

        {!filtered ? (
          <p className="text-ivory/70">{t("blog.loading")}</p>
        ) : filtered.length === 0 ? (
          <p className="text-ivory/70 text-center py-16" data-testid="blog-empty">
            {query.trim()
              ? (lang === "fr" ? `Aucun article ne correspond à « ${query.trim()} ».` : `No articles match "${query.trim()}".`)
              : t("blog.empty")}
          </p>
        ) : (
          <>
            {query.trim() && (
              <p className="text-xs text-ivory/55 mb-6" data-testid="blog-results-count">
                {lang === "fr"
                  ? `${filtered.length} article${filtered.length > 1 ? "s" : ""} trouvé${filtered.length > 1 ? "s" : ""}`
                  : `${filtered.length} article${filtered.length > 1 ? "s" : ""} found`}
              </p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {shown.map((p, i) => {
                const l = lp(p);
                return (
                  <FadeUp key={p.id} delay={Math.min(i, 8) * 0.06}>
                    <Link
                      to={`/blogue/${p.slug}`}
                      data-testid={`blog-card-${p.slug}`}
                      className={`group block bg-surface border rounded-xl overflow-hidden transition-colors duration-500 h-full ${p.pinned ? "border-gold/50 shadow-[0_0_24px_rgba(212,175,55,0.12)]" : "border-ivory/10 hover:border-gold/30"}`}
                    >
                      {p.image && (
                        <div className="overflow-hidden h-52 relative">
                          <img src={p.image} alt={l.title} loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                          {p.pinned && (
                            <span data-testid={`blog-pinned-${p.slug}`}
                              className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-gold text-ink text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full">
                              <Pin className="w-3 h-3" />{lang === "fr" ? "Épinglé" : "Pinned"}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="p-6">
                        {p.pinned && !p.image && (
                          <span data-testid={`blog-pinned-${p.slug}`}
                            className="inline-flex items-center gap-1.5 bg-gold text-ink text-[10px] font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-3">
                            <Pin className="w-3 h-3" />{lang === "fr" ? "Épinglé" : "Pinned"}
                          </span>
                        )}
                        <p className="text-xs text-ivory/65 mb-2">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { timeZone: "America/Montreal", year: "numeric", month: "long", day: "numeric" }) : ""}
                        </p>
                        <h2 className="font-serif text-2xl mb-3 group-hover:text-gold transition-colors duration-300">{l.title}</h2>
                        {l.excerpt && <p className="text-sm text-ivory/60 leading-relaxed line-clamp-3 mb-4">{l.excerpt}</p>}
                        <span className="inline-flex items-center gap-2 text-gold text-sm">
                          {t("blog.read_more")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                        </span>
                      </div>
                    </Link>
                  </FadeUp>
                );
              })}
            </div>

            {/* Pagination — 25 articles par page */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-16" data-testid="blog-pagination">
                <button
                  onClick={() => goPage(cur - 1)} disabled={cur <= 1}
                  data-testid="blog-page-prev"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-ivory/15 text-xs text-ivory/70 hover:border-gold/60 hover:text-gold transition-colors duration-300 disabled:opacity-30 disabled:hover:border-ivory/15 disabled:hover:text-ivory/70">
                  <ChevronLeft className="w-3.5 h-3.5" />{lang === "fr" ? "Précédent" : "Previous"}
                </button>
                {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => goPage(n)} data-testid={`blog-page-${n}`}
                    className={`w-9 h-9 rounded-full text-xs transition-colors duration-300 ${n === cur ? "bg-gold text-ink font-semibold" : "border border-ivory/15 text-ivory/70 hover:border-gold/60 hover:text-gold"}`}>
                    {n}
                  </button>
                ))}
                <button
                  onClick={() => goPage(cur + 1)} disabled={cur >= pages}
                  data-testid="blog-page-next"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-ivory/15 text-xs text-ivory/70 hover:border-gold/60 hover:text-gold transition-colors duration-300 disabled:opacity-30 disabled:hover:border-ivory/15 disabled:hover:text-ivory/70">
                  {lang === "fr" ? "Suivant" : "Next"}<ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
